import React, { useState, useEffect, useRef } from 'react';
import {
  Globe,
  Upload,
  RefreshCw,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Clock,
  Download,
  Search,
  ChevronDown,
  ChevronUp,
  GraduationCap,
  MapPin,
  Award,
  BookOpen,
} from 'lucide-react';
import {
  fetchCountries,
  fetchSeriesBac,
  fetchProvinces,
  fetchGrades,
  fetchDiplomes,
  fetchUploadHistory,
  uploadCountriesExcel,
  uploadSeriesBacExcel,
  uploadProvincesExcel,
  uploadGradesExcel,
  uploadDiplomesExcel,
  clearNomenclatureCache,
  type NomenclatureCountry,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureGrade,
  type NomenclatureDiplome,
  type UploadHistory,
} from '@/services/nomenclatureService';
import { useAuthStore } from '@/features/auth/stores/authStore';

type NomenclatureType = 'countries' | 'seriesBac' | 'provinces' | 'grades' | 'diplomes';

interface TabConfig {
  id: NomenclatureType;
  label: string;
  icon: React.ReactNode;
  description: string;
  instructions: string[];
}

const TABS: TabConfig[] = [
  {
    id: 'countries',
    label: 'Pays',
    icon: <Globe className="w-5 h-5" />,
    description: 'Nomenclature des pays',
    instructions: [
      'Première colonne : Liste des noms de pays (en-tête peut être n\'importe quel titre)',
      'Une ligne par pays (nom en français)',
      'Le Maroc sera automatiquement placé en première position avec le code "MA"',
    ],
  },
  {
    id: 'seriesBac',
    label: 'Série Bac',
    icon: <GraduationCap className="w-5 h-5" />,
    description: 'Séries du Baccalauréat',
    instructions: [
      'Colonne A : Code de la série (ex: SM, SVT, SC, etc.)',
      'Colonne B : Libellé en français (ex: Sciences Mathématiques)',
      'Colonne C : Libellé en arabe',
      'Colonne D : Type (ex: Sciences, Lettres, Technique, etc.)',
    ],
  },
  {
    id: 'provinces',
    label: 'Provinces',
    icon: <MapPin className="w-5 h-5" />,
    description: 'Provinces du Maroc',
    instructions: [
      'Une seule colonne : Province',
      'Une ligne par province (nom en français)',
    ],
  },
  {
    id: 'grades',
    label: 'Grades',
    icon: <Award className="w-5 h-5" />,
    description: 'Grades académiques',
    instructions: [
      'Une seule colonne : Grade',
      'Une ligne par grade (ex: Professeur, Assistant, etc.)',
    ],
  },
  {
    id: 'diplomes',
    label: 'Diplômes',
    icon: <BookOpen className="w-5 h-5" />,
    description: 'Types de diplômes',
    instructions: [
      'Une seule colonne : Diplôme',
      'Une ligne par diplôme (ex: Licence, Master, Doctorat, etc.)',
    ],
  },
];

export const NomenclaturePage: React.FC = () => {
  const { tokens } = useAuthStore();
  const token = tokens?.accessToken;
  const [activeTab, setActiveTab] = useState<NomenclatureType>('countries');
  const [countries, setCountries] = useState<NomenclatureCountry[]>([]);
  const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
  const [provinces, setProvinces] = useState<NomenclatureProvince[]>([]);
  const [grades, setGrades] = useState<NomenclatureGrade[]>([]);
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [uploadHistory, setUploadHistory] = useState<UploadHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    setSearchTerm('');

    try {
      // Try to fetch upload history, but don't fail if it doesn't work
      try {
        const historyData = await fetchUploadHistory(activeTab, token || undefined);
        setUploadHistory(historyData);
      } catch {
        setUploadHistory([]);
      }

      switch (activeTab) {
        case 'countries':
          const countriesData = await fetchCountries(true);
          setCountries(countriesData);
          break;
        case 'seriesBac':
          const seriesBacData = await fetchSeriesBac(true);
          setSeriesBac(seriesBacData);
          break;
        case 'provinces':
          const provincesData = await fetchProvinces(true);
          setProvinces(provincesData);
          break;
        case 'grades':
          const gradesData = await fetchGrades(true);
          setGrades(gradesData);
          break;
        case 'diplomes':
          const diplomesData = await fetchDiplomes(true);
          setDiplomes(diplomesData);
          break;
      }
    } catch (err) {
      console.error('Error loading nomenclature data:', err);
      // Don't show error for empty data - it's expected for new nomenclatures
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    if (!token) {
      setError('No auth token available');
      setIsUploading(false);
      return;
    }

    try {
      let result;
      switch (activeTab) {
        case 'countries':
          result = await uploadCountriesExcel(file, token);
          break;
        case 'seriesBac':
          result = await uploadSeriesBacExcel(file, token);
          break;
        case 'provinces':
          result = await uploadProvincesExcel(file, token);
          break;
        case 'grades':
          result = await uploadGradesExcel(file, token);
          break;
        case 'diplomes':
          result = await uploadDiplomesExcel(file, token);
          break;
      }
      setSuccess(result.message);
      clearNomenclatureCache();
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'countries':
        return countries;
      case 'seriesBac':
        return seriesBac;
      case 'provinces':
        return provinces;
      case 'grades':
        return grades;
      case 'diplomes':
        return diplomes;
      default:
        return [];
    }
  };

  const getFilteredData = () => {
    const data = getCurrentData();
    if (!searchTerm) return data;

    const term = searchTerm.toLowerCase();
    return data.filter((item: any) => {
      if ('nameFr' in item) {
        return item.nameFr.toLowerCase().includes(term) || (item.code && item.code.toLowerCase().includes(term));
      }
      if ('labelFr' in item) {
        return item.labelFr.toLowerCase().includes(term) || (item.code && item.code.toLowerCase().includes(term));
      }
      return item.code ? item.code.toLowerCase().includes(term) : false;
    });
  };

  const getDataCount = () => {
    switch (activeTab) {
      case 'countries':
        return countries.length;
      case 'seriesBac':
        return seriesBac.length;
      case 'provinces':
        return provinces.length;
      case 'grades':
        return grades.length;
      case 'diplomes':
        return diplomes.length;
      default:
        return 0;
    }
  };

  const getCountLabel = () => {
    switch (activeTab) {
      case 'countries':
        return 'pays';
      case 'seriesBac':
        return 'séries';
      case 'provinces':
        return 'provinces';
      case 'grades':
        return 'grades';
      case 'diplomes':
        return 'diplômes';
      default:
        return 'éléments';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activeTabConfig = TABS.find((t) => t.id === activeTab)!;
  const filteredData = getFilteredData();

  const renderTableHeaders = () => {
    switch (activeTab) {
      case 'countries':
        return (
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom (Français)</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
          </tr>
        );
      case 'seriesBac':
        return (
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé (Français)</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé (Arabe)</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
          </tr>
        );
      case 'provinces':
        return (
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Province</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
          </tr>
        );
      case 'grades':
        return (
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
          </tr>
        );
      case 'diplomes':
        return (
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diplôme</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
          </tr>
        );
    }
  };

  const renderTableRow = (item: any, index: number) => {
    switch (activeTab) {
      case 'countries':
        const country = item as NomenclatureCountry;
        return (
          <tr key={country.id} className={`hover:bg-gray-50 ${country.code === 'MA' ? 'bg-primary-50' : ''}`}>
            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
            <td className="px-4 py-3">
              <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{country.code}</span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-900">
              {country.nameFr}
              {country.code === 'MA' && <span className="ml-2 text-xs text-primary-600">(par défaut)</span>}
            </td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${country.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {country.isActive ? 'Actif' : 'Inactif'}
              </span>
            </td>
          </tr>
        );

      case 'seriesBac':
        const serie = item as NomenclatureSerieBac;
        return (
          <tr key={serie.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
            <td className="px-4 py-3">
              <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{serie.code}</span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-900">{serie.labelFr}</td>
            <td className="px-4 py-3 text-sm text-gray-600" dir="rtl">{serie.labelAr || '-'}</td>
            <td className="px-4 py-3 text-sm text-gray-600">{serie.type || '-'}</td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${serie.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {serie.isActive ? 'Actif' : 'Inactif'}
              </span>
            </td>
          </tr>
        );

      case 'provinces':
        const province = item as NomenclatureProvince;
        return (
          <tr key={province.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{province.nameFr}</td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${province.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {province.isActive ? 'Actif' : 'Inactif'}
              </span>
            </td>
          </tr>
        );

      case 'grades':
        const grade = item as NomenclatureGrade;
        return (
          <tr key={grade.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{grade.labelFr}</td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${grade.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {grade.isActive ? 'Actif' : 'Inactif'}
              </span>
            </td>
          </tr>
        );

      case 'diplomes':
        const diplome = item as NomenclatureDiplome;
        return (
          <tr key={diplome.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{diplome.labelFr}</td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${diplome.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {diplome.isActive ? 'Actif' : 'Inactif'}
              </span>
            </td>
          </tr>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Nomenclatures</h1>
          <p className="text-gray-500 mt-1">Gérez les nomenclatures E-DEP (pays, séries bac, provinces, etc.)</p>
        </div>
        <button
          onClick={loadData}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 bg-primary-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">{activeTabConfig.icon}</div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{activeTabConfig.description}</h2>
                <p className="text-sm text-gray-500">
                  {getDataCount()} {getCountLabel()} dans la base de données
                </p>
              </div>
            </div>

            {/* Upload Button */}
            <label className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 disabled:opacity-50">
              {isUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Importation...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Importer Excel
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Format du fichier Excel attendu :</p>
              <ul className="list-disc list-inside space-y-1">
                {activeTabConfig.instructions.map((instruction, idx) => (
                  <li key={idx}>{instruction}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Rechercher...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {getDataCount() === 0
                ? `Aucun ${getCountLabel().slice(0, -1)} importé. Importez un fichier Excel pour commencer.`
                : `Aucun résultat trouvé pour "${searchTerm}"`}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">{renderTableHeaders()}</thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.map((item: any, index: number) => renderTableRow(item, index))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-gray-900">Historique des imports - {activeTabConfig.label}</span>
            <span className="text-sm text-gray-500">({uploadHistory.length} entrées)</span>
          </div>
          {showHistory ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {showHistory && (
          <div className="border-t border-gray-200">
            {uploadHistory.length === 0 ? (
              <div className="p-6 text-center text-gray-500">Aucun historique d'import pour cette nomenclature</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fichier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enregistrements</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {uploadHistory.map((upload) => (
                    <tr key={upload.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(upload.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{upload.fileName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {upload.recordCount} {getCountLabel()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{upload.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Download Template */}
      <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Besoin d'un modèle de fichier ?</h3>
          <p className="text-sm text-gray-500">
            Téléchargez le template Excel pour la nomenclature {activeTabConfig.label.toLowerCase()}
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-primary-600 bg-white border border-primary-300 rounded-lg hover:bg-primary-50">
          <Download className="w-4 h-4" />
          Télécharger le template
        </button>
      </div>
    </div>
  );
};

export default NomenclaturePage;
